import { useAuth0 } from '@auth0/auth0-react';
import { QProfileHeader, QProfileItem, QProfileMenu, QProfileSubHeader, QThemeProvider, QButton, QTypography } from '@kw/quokka-ui';
import { useNavigate } from 'react-router-dom';
import { Box, Divider } from '@mui/material';
import React from 'react';
import { height } from '@mui/system';
import { useFetchService } from '../../utils/fetchService';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useStickyResult } from '../../utils/useStickyResult';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { logoutLocal } from '../../auth';

interface IProps {
  anchorEl: HTMLDivElement;
  handleClose: () => void;
}

export const useOrganization = (accessToken: string, shouldFetch = true) => {
  const { data: organizationData, error: organizationError, mutate } = useFetchService(`/organizations/`, accessToken, shouldFetch);
  const orgData = useStickyResult(organizationData);
  const isLoading = !orgData && !organizationError;
  return { orgData, organizationError, isLoading, mutate };
};

export default function SettingsDropDownMenu({ anchorEl, handleClose }: IProps) {
  const settingsOpen = Boolean(anchorEl);
  const settingsId = settingsOpen ? 'simple-popover' : undefined;
  const auth0 = useAuth0();
  const { isAuthenticated, isLoading, user } = auth0;
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { orgData, organizationError } = useOrganization(accessToken);
  const canEditSettings: boolean = verifyAuthPermission(AuthPermissions.MENU_SETTINGS);
  const [copiedState, setCopiedState] = React.useState(false);
  const navigate = useNavigate();

  return (
    isAuthenticated &&
    !isLoading && (
      <QThemeProvider>
        <QProfileMenu
          id={settingsId}
          anchorEl={anchorEl}
          open={settingsOpen}
          onClose={handleClose}
          header={<QProfileHeader primary={user?.name ?? ''} secondary={user?.email ?? ''} />}
          aria-label="settings drop down menu"
          subHeaders={
            <>
              <Divider />
              <QTypography variant="smallRegular">{(orgData && !organizationError && orgData?.name) ?? ''}</QTypography>

              <Box display="flex" flexDirection="row" key="copy_org_id" sx={{ marginTop: '8px' }}>
                {copiedState ? (
                  <QTypography variant="smallMedium" sx={{ color: '#2753CF' }}>
                    Organization ID copied!
                  </QTypography>
                ) : (
                  <QButton
                    sx={{ padding: '0px', color: '#2753CF', height: '20px' }}
                    onClick={() => {
                      navigator.clipboard.writeText(orgData?.id);
                      setCopiedState(true);
                      setTimeout(() => setCopiedState(false), 5000);
                    }}
                  >
                    <>
                      <img alt="search-icon" src={'/icons/copy.svg'} />

                      <QTypography marginLeft={'4px'} variant="smallMedium" sx={{ color: '#2753CF' }}>
                        Copy Organization ID
                      </QTypography>
                    </>
                  </QButton>
                )}
              </Box>
            </>
          }
          items={[
            ...(canEditSettings
              ? [
                  {
                    page: 'Settings',
                    onClick: () => {
                      navigate('/settings');
                      handleClose();
                    },
                  },
                ]
              : []),
            {
              page: 'Logout',
              onClick: () => logoutLocal(auth0),
            },
          ].map((item, index) => (
            <QProfileItem onClick={() => item.onClick()} key={index}>
              {item.page}
            </QProfileItem>
          ))}
        />
      </QThemeProvider>
    )
  );
}
